import React from "react";
import Template1 from "../../templates/Template1";
import Template2 from "../../templates/Template2";
import Template3 from "../../templates/Template3";
import DynamicTemplate from "../../templates/DynamicTemplate";
import { Box, Paper, Divider, useMediaQuery, useTheme } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useStore } from "../../store";
import { RefreshCw } from "lucide-react";
import axios from "axios";
import swal from "sweetalert";
import Cookies from "js-cookie";
import { dynamicTemplateIds, getDynamicTemplateConfig } from "../../templates/templateCatalog";

const ResumePreview = ({ template, downloadable = false }) => {
  const { data, setData, skills, setSkills, userData, setUserData } =
    useStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const suggestedMissionsSection = data?.sections?.suggested_missions;
  const suggestedMissions = suggestedMissionsSection?.items || [];
  const suggestedSkillsTitle =
    "Suggested Skills";
  const suggestedMissionsTitle = "Suggested Missions";
  const suggestedSkills = skills?.list1 || [];

  const handleDragEnd = (result) => {
    const { destination, source } = result;

    if (!destination) return;

    if (
      destination.index === source.index &&
      destination.droppableId === source.droppableId
    ) {
      return;
    }

    const list1 = skills.list1 || [];
    const list2 = skills.list2 || [];
    const list3 = data?.sections?.suggested_missions?.items || [];
    const list4 = (data?.sections?.experience?.items || []).map(
      (exp) => exp.missions || []
    );

    const sourceListId = source.droppableId;
    const destinationListId = destination.droppableId;

    const getList = (droppableId) => {
      if (droppableId === "list1") return list1;
      if (droppableId === "list2") return list2;
      if (droppableId === "list3") return list3;
      if (droppableId.startsWith("list4")) {
        const index = parseInt(droppableId.split("-")[1], 10);
        return list4[index];
      }
      return [];
    };

    const sourceList = getList(sourceListId);
    const destinationList = getList(destinationListId);

    if (!sourceList || !destinationList) return;

    const [movedItem] = sourceList.splice(source.index, 1);

    destinationList.splice(destination.index, 0, movedItem);

    if (destinationListId === "list1" || destinationListId === "list2") {
      if (sourceListId === "list1" || destinationListId === "list1") {
        setSkills({ ...skills, list1 });
      }
      if (sourceListId === "list2" || destinationListId === "list2") {
        setSkills({ ...skills, list2 });
      }
    } else {
      if (sourceListId === "list3" || destinationListId === "list3") {
        setData({
          ...data,
          sections: {
            ...data.sections,
            suggested_missions: {
              ...data.sections?.suggested_missions,
              items: list3,
            },
          },
        });
      }
      if (
        sourceListId.startsWith("list4") ||
        destinationListId.startsWith("list4")
      ) {
        const updatedExperienceItems = data.sections.experience.items.map(
          (item, idx) => ({
            ...item,
            missions:
              idx === parseInt(sourceListId.split("-")[1], 10)
                ? sourceList
                : idx === parseInt(destinationListId.split("-")[1], 10)
                ? destinationList
                : item.missions,
          })
        );
        setData({
          ...data,
          sections: {
            ...data.sections,
            experience: {
              ...data.sections.experience,
              items: updatedExperienceItems,
            },
          },
        });
      }
      setSkills({ list1, list2 });
    }
  };

  const handleRefresh = async () => {
    try {
      const willProceed = await swal({
        title: "Confirm Action",
        text: "This action will deduct 0.5 credit from your account. Do you want to proceed?",
        icon: "warning",
        buttons: ["Cancel", "Yes, proceed!"],
        dangerMode: true,
      });
      const token = Cookies.get("interviewaxis_session");

      if (!willProceed) return;
      const creditResponse = await axios.post(
        "https://www.interviewaxis.com/api/v1/checkCreditdeduct",
        { email: userData.user_email },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (creditResponse.data.credit === "no") {
        const tokenResponse = await axios.post(
          "https://api.interviewaxis.com/modifier/get_token",
          {
            user_id: userData.user_id,
            user_email: userData.user_email,
          },
          { headers: { Accept: "application/json" } }
        );

        localStorage.setItem("token", tokenResponse.data.access_token);

        await swal({
          title: "Insufficient Credits",
          text: "You don't have enough credits. Please purchase a plan to continue.",
          icon: "error",
          button: "Go to Pricing",
        });

        window.location.href = `https://interviewaxis.com/pricing-plan?mid=${tokenResponse.data.access_token}`;
        return;
      }

      const data = JSON.parse(localStorage.getItem("resume_data"));
      const text = JSON.parse(localStorage.getItem("job_description"));
      const extracted_text = JSON.parse(localStorage.getItem("extracted_text"));
      const responseUpdateData = await axios.post(
        "https://api.interviewaxis.com/modifier/update_data",
        {
          job_description: text,
          resume_json: data,
          extracted_text: extracted_text,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      setData(responseUpdateData.data);
      localStorage.setItem(
        "resume_data",
        JSON.stringify(responseUpdateData.data)
      );

      await swal({
        title: "Success!",
        text: "Suggestions refreshed successfully!",
        icon: "success",
        button: "OK",
      });
    } catch (error) {
      console.error("Error:", error);
      await swal({
        title: "Error",
        text: "An error occurred. Please try again later.",
        icon: "error",
        button: "OK",
      });
    }
  };

  const renderTemplate = () => {
    if (template === "Template1") {
      return <Template1 data={data} skills={skills} downloadable={downloadable} />;
    }

    if (template === "Template2") {
      return <Template2 data={data} skills={skills} downloadable={downloadable} />;
    }

    if (template === "Template3") {
      return <Template3 data={data} skills={skills} downloadable={downloadable} />;
    }

    if (dynamicTemplateIds.has(template)) {
      return (
        <DynamicTemplate
          data={data}
          skills={skills}
          downloadable={downloadable}
          config={getDynamicTemplateConfig(template)}
        />
      );
    }

    return <Template1 data={data} skills={skills} downloadable={downloadable} />;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: 3,
        bgcolor: "white",
      }}
    >
      <DragDropContext onDragEnd={handleDragEnd}>
        <Paper
          elevation={3}
          sx={{ order: isMobile ? "2" : "" }}
          style={{ height: "600px", overflowY: "auto" }}
        >
          {renderTemplate()}
        </Paper>
        {!downloadable && (
          <Paper
            elevation={3}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Box
              elevation={3}
              sx={{ display: "flex", width: isMobile ? "100%" : "600px" }}
            >
              <Droppable droppableId="list3">
                {(provided) => (
                  <ul
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{ width: "100%" }}
                  >
                    <Box
                      style={{
                        padding: "24px",
                        backgroundColor: "#002855",
                        width: "100%",
                        color: "white",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <h2>{suggestedMissionsTitle}</h2>
                      <div
                        onClick={handleRefresh}
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <RefreshCw size={24} color="white" />
                      </div>
                    </Box>
                    <Box
                      sx={{ p: 3, pt: 1 }}
                      style={{
                        minHeight: "300px",
                        maxHeight: "300px",
                        overflowY: "auto",
                      }}
                    >
                      {suggestedMissions.map(
                        (suggested_mission, index) => (
                          <React.Fragment key={suggested_mission}>
                            <Draggable
                              draggableId={`list3-${suggested_mission}`}
                              index={index}
                            >
                              {(provided) => (
                                <li
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  {suggested_mission}
                                </li>
                            )}
                          </Draggable>
                          {index !==
                              suggestedMissions.length -
                                1 && <Divider />}
                          </React.Fragment>
                        )
                      )}
                    </Box>
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </Box>
            <Box
              elevation={3}
              sx={{
                display: "flex",
                width: isMobile ? "100%" : "600px",
              }}
              className="suggested-box"
            >
              <Droppable droppableId="list1">
                {(provided) => (
                  <ul
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{ width: "100%" }}
                  >
                    <h2
                      style={{
                        padding: "24px",
                        backgroundColor: "#002855",
                        width: "100%",
                        color: "white",
                      }}
                    >
                      {suggestedSkillsTitle}
                    </h2>
                    <Box sx={{ p: 3, pt: 1 }}>
                      {suggestedSkills.map((skill, index) => (
                        <React.Fragment key={skill}>
                          <Draggable
                            draggableId={`list1-${skill}`}
                            index={index}
                          >
                            {(provided) => (
                              <li
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                {skill}
                              </li>
                            )}
                          </Draggable>
                          {index !== suggestedSkills.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </Box>
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </Box>
          </Paper>
        )}
      </DragDropContext>
    </Box>
  );
};

export default ResumePreview;
